import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp } from "../lib/brand";
import { INSIGHTS } from "../data/insights";
import { Eyebrow } from "../components/site/Primitives";
import { PageHero, CTASection } from "../components/site/Sections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stagger = (i) => ({ ...fadeInUp, transition: { ...fadeInUp.transition, delay: 0.07 * i } });

const Subscribe = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. New insights will arrive in your inbox.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg === "Email already subscribed" ? "This email is already subscribed." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div {...stagger(1)} data-testid="insights-subscribe" className="bg-summit-forest text-white p-8 md:p-10 grain relative">
      <div className="relative">
        <Eyebrow light className="mb-4">Stay Informed</Eyebrow>
        <h3 className="font-heading text-3xl font-light leading-tight">Receive new insights as they're published.</h3>
        <p className="mt-3 font-body text-sm text-white/70 leading-relaxed">Occasional, substantive perspectives. No noise.</p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="subscribe-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/40 px-4 py-3 font-body text-sm focus:outline-none focus:border-summit-gold"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="subscribe-submit-btn"
            className="inline-flex items-center justify-center gap-2 bg-summit-gold hover:bg-summit-gold-dark text-summit-charcoal hover:text-white px-6 py-3 font-body text-sm font-medium transition-colors disabled:opacity-60"
          >
            {loading ? "Subscribing…" : "Subscribe"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </motion.div>
  );
};

export default function Insights() {
  useSEO(
    "Insights on AI Adoption, Transformation & Leadership",
    "Executive perspectives from Summit Transformation Group on AI strategy, organizational AI adoption, AI governance, transformation, and leading through change."
  );

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Perspectives for leaders navigating AI and transformation."
        copy="Practical thinking on the organizational side of AI adoption, transformation, and leadership—written for executives."
        testId="insights-hero"
      />

      <section data-testid="insights-list" className="py-24 md:py-32 bg-summit-bg border-t border-summit-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 divide-y divide-summit-border">
            {INSIGHTS.map((p, i) => (
              <motion.article key={p.slug} {...stagger(i)} data-testid={`insight-item-${i}`} className="py-10 first:pt-0 group">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="font-body text-xs uppercase tracking-[0.18em] text-summit-gold-dark">{p.category}</span>
                  <span className="text-summit-border">·</span>
                  <span className="font-body text-xs text-summit-text-muted">{p.readTime}</span>
                  {p.placeholder && (
                    <span data-testid={`insight-placeholder-badge-${i}`} className="ml-auto font-body text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-summit-gold/60 text-summit-gold-dark">
                      Coming soon
                    </span>
                  )}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-light leading-tight text-summit-charcoal group-hover:text-summit-gold-dark transition-colors">{p.title}</h2>
                <p className="mt-4 font-body text-base text-summit-text-muted leading-relaxed max-w-2xl">{p.excerpt}</p>
              </motion.article>
            ))}
          </div>
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-8">
              <Subscribe />
              <motion.div {...stagger(2)} className="border border-summit-border p-8">
                <Eyebrow className="mb-4">Topics</Eyebrow>
                <ul className="space-y-2.5">
                  {["AI strategy and adoption", "AI readiness and governance", "Organizational transformation", "Strategy execution", "Leading through change"].map((t) => (
                    <li key={t} className="flex items-center gap-3 font-body text-sm text-summit-charcoal"><span className="w-1.5 h-1.5 rotate-45 bg-summit-gold" />{t}</li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </aside>
        </div>
      </section>

      <CTASection heading="Want to discuss how these ideas apply to your organization?" primaryLabel="Talk With Summit" testId="insights-cta" />
    </>
  );
}
